import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import localStyles from './GuideItem.module.pcss';
import GuideAccordionToggle from '../GuideAccordionToggle/GuideAccordionToggle';
import ArticleLink, { LinkStyleType } from '../ArticleLink/ArticleLink';
import ChapterItem from '../ChapterItem/ChapterItem';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import guideContentQuery from '../../GuideContentManagement/Guide.query.graphql';
import type {
  GuideQuery,
  GuideQueryVariables,
  GuideViewFragment
} from '../../../../types/graphql-types';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';

/**
 * Props for the GuideItem component.
 */
interface Props {
  /**
   * The node representing the guide.
   */
  guide: GuideViewFragment;

  /**
   * An object representing the active toggle state for guide items.
   */
  isActiveToggle: { [key: string]: boolean };

  /**
   * A function to toggle the active state of guide items.
   * @param id - The ID of the guide item to toggle.
   */
  toggleItem: (id: string) => void;

  /**
   * The ID of the selected guide associated with the article.
   */
  selectedGuideId: string;
}

/**
 * The guide item component
 *
 * @author Amit Agrawal
 */
const GuideItem: React.FC<React.PropsWithChildren<Props>> = ({
  guide,
  isActiveToggle,
  toggleItem,
  selectedGuideId
}) => {
  const cx = useClassNameMapper(localStyles);
  const {
    contextMessage: { id: contextMessageId, uid, board }
  } = useContext(AppContext);

  const {
    publicConfig: { maxGuidesAndContentLimit }
  } = useContext(TenantContext);

  const isCurrentActiveToggle = isActiveToggle[guide.id] ?? false;

  const queryResult = useQueryWithTracing<GuideQuery, GuideQueryVariables>(
    module,
    guideContentQuery,
    {
      variables: {
        id: guide.id,
        useGuide: true,
        first: maxGuidesAndContentLimit
      },
      skip: !isActiveToggle[guide.id]
    }
  );

  const guideContents = queryResult.data?.guide?.contents;

  return (
    <>
      <GuideAccordionToggle
        id={guide.id}
        title={guide.title}
        isOpen={isCurrentActiveToggle}
        onClick={async () => {
          toggleItem(guide.id);
        }}
      >
        {guideContents?.edges.map(({ node: content }) => {
          return content.__typename === 'KBArticle' ? (
            <ArticleLink
              key={content.message.id}
              article={{ ...content.message, board, uid }}
              selectedGuideId={selectedGuideId}
              parentGuideId={guide.id}
              useShallow={true}
              className={cx('lia-guide-article')}
              linkStyleType={LinkStyleType.CONTENT}
              contextMessageId={contextMessageId}
              additionalQueryParams={{
                [EndUserQueryParams.NODE_ID]: content.message.board.id,
                [EndUserQueryParams.ARTICLE_ID]: content.message.id
              }}
            />
          ) : (
            <ChapterItem
              key={content.id}
              chapter={content}
              isActive={isActiveToggle[content.id]}
              toggleItem={toggleItem}
              selectedGuideId={selectedGuideId}
            />
          );
        })}
      </GuideAccordionToggle>
    </>
  );
};

export default GuideItem;
